import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flower2, Sparkles, Share2, Palette, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import { startLogin } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1561410020-f484d7db6b3e?auto=format&fit=crop&w=1200&q=80";
const STUDIO = "https://images.pexels.com/photos/5414007/pexels-photo-5414007.jpeg?auto=compress&cs=tinysrgb&w=1200";

const steps = [
  { icon: Palette, title: "Choose your palette", desc: "Pick blooms and pair each with the perfect wedding hue." },
  { icon: Sparkles, title: "Generate with AI", desc: "Watch a photorealistic bouquet come to life in seconds." },
  { icon: Share2, title: "Save & share", desc: "Get an instant florist quote and a shareable link." },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-6">
              <Flower2 className="w-4 h-4" /> Virtual Bouquet Studio
            </span>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight font-medium">
              Design the bouquet of your <span className="italic text-primary">dreams.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
              Combine your favourite flowers and colours, then let AI craft a photorealistic wedding bouquet — and get an <span className="font-medium text-foreground">obligation-free quote</span> in minutes. No pushy florists, no commitment.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/studio"
                data-testid="hero-start-btn"
                className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all shadow-sm hover:shadow-lg"
              >
                Start designing
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              {!user && (
                <button
                  onClick={startLogin}
                  data-testid="hero-signin-btn"
                  className="rounded-full px-7 py-3.5 font-medium border border-primary text-primary hover:bg-primary/5 transition-all"
                >
                  Sign in to save
                </button>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-secondary/20 rounded-[2.5rem] blur-2xl" />
            <img
              src={HERO}
              alt="Bridal bouquet"
              className="relative rounded-[2rem] shadow-2xl object-cover w-full h-[520px] animate-float"
            />
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white rounded-2xl border border-border p-8 hover:-translate-y-1 hover:shadow-md transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <s.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-serif-display text-2xl mb-2">{s.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative rounded-[2rem] overflow-hidden">
          <img src={STUDIO} alt="Florist studio" className="w-full h-[360px] object-cover" />
          <div className="absolute inset-0 bg-primary/40" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white max-w-2xl">
              Every love story deserves the perfect flowers.
            </h2>
            <Link
              to="/studio"
              data-testid="cta-start-btn"
              className="mt-8 rounded-full px-8 py-3.5 font-medium bg-white text-primary hover:bg-[#FDFBF7] transition-all shadow-lg"
            >
              Open the studio
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        <p className="font-serif-display text-lg text-foreground">Bloom &amp; Vow</p>
        <p className="mt-1">Crafted for couples in love.</p>
      </footer>
    </div>
  );
}
